// ============================================
// FILE: src/components/modals/AddAddressModal.jsx
// ============================================
//
// Marketplace-style address form: CONTACT DETAILS, then ADDRESS, then the
// save-as chips. Presentation only — the field names, validation rules and
// onSave(formData) payload are unchanged, as is the Google Map picker.

import React, { useState, useEffect } from 'react';
import { MapPin, X, ArrowLeft } from 'lucide-react';
import { mockSuggestions } from '../../data/products';
import MapPickerModal from '../MapPickerModal';

const BLANK = {
  label: '',
  recipient_name: '',
  landmark: '',
  address: '',
  city: 'Gurgaon',
  state: 'Haryana',
  country: 'India',
  pincode: '',
  phone: '',
  latitude: '',
  longitude: '',
};

const AddAddressModal = ({ isOpen, onClose, onSave, editAddress }) => {
  const [formData, setFormData] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  // Update formData when editAddress changes
  useEffect(() => {
    if (editAddress) {
      setFormData({
        label: editAddress.label || '',
        recipient_name: editAddress.recipient_name || '',
        landmark: editAddress.landmark || '',
        address: editAddress.address || '',
        city: editAddress.city || 'Gurgaon',
        state: editAddress.state || 'Haryana',
        country: editAddress.country || 'India',
        pincode: editAddress.pincode != null ? String(editAddress.pincode) : '',
        phone: editAddress.phone != null ? String(editAddress.phone) : '',
        latitude: editAddress.latitude || '',
        longitude: editAddress.longitude || '',
      });
    } else {
      setFormData(BLANK);
    }
    setErrors({});
  }, [editAddress]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.label.trim()) newErrors.label = 'Label is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.pincode || formData.pincode.length !== 6) {
      newErrors.pincode = 'Valid 6-digit pincode required';
    }
    if (!formData.phone || formData.phone.length !== 10) {
      newErrors.phone = 'Valid 10-digit phone required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleMapConfirm = (data) => {
    setFormData((prev) => ({
      ...prev,
      address: data.address || prev.address,
      city: data.city || prev.city,
      state: data.state || prev.state,
      pincode: data.pincode || prev.pincode,
      country: data.country || prev.country,
      latitude: data.latitude ?? prev.latitude,
      longitude: data.longitude ?? prev.longitude,
    }));
    setShowMapPicker(false);
  };

  const searchAddress = (query) => {
    setFormData({ ...formData, address: query });
    if (errors.address) setErrors({ ...errors, address: '' });
    if (query.length < 3) {
      setShowAddressSuggestions(false);
      return;
    }
    setAddressSuggestions(mockSuggestions(query));
    setShowAddressSuggestions(false);
  };

  const selectSuggestion = (suggestion) => {
    setFormData({
      ...formData,
      address: suggestion.address,
      city: suggestion.city,
      pincode: suggestion.pincode,
    });
    setShowAddressSuggestions(false);
  };

  const field = (name, label, extra = {}) => (
    <div className="fx-field">
      <label htmlFor={`addr-${name}`}>{label}</label>
      <input
        id={`addr-${name}`}
        type="text"
        name={name}
        value={formData[name]}
        onChange={extra.onChange || handleChange}
        placeholder={extra.placeholder}
        maxLength={extra.maxLength}
        inputMode={extra.inputMode}
        className="fx-input"
        data-invalid={Boolean(errors[name])}
      />
      {errors[name] && <p className="fx-error">{errors[name]}</p>}
    </div>
  );

  return (
    <>
      <div className="fashion-scope fixed inset-0 z-[310] bg-[#fafafa] overflow-y-auto">
        <div className="max-w-[560px] mx-auto min-h-full bg-white">

          {/* Header */}
          <div
            className="sticky top-0 z-10 bg-white flex items-center gap-3 px-4 py-3"
            style={{ borderBottom: '1px solid var(--fx-line)' }}
          >
            <button onClick={onClose} className="p-1 -ml-1" aria-label="Go back">
              <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
            </button>
            <span className="fx-label flex-1">
              {editAddress ? 'Edit Address' : 'Add New Address'}
            </span>
            <button onClick={onClose} className="p-1 -mr-1" aria-label="Close">
              <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-4 py-5 pb-28">

            {/* ---- Contact details ---- */}
            <h3
              className="mb-4"
              style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fx-ink-2)' }}
            >
              Contact Details
            </h3>

            {field('recipient_name', 'Name', { placeholder: 'Name of the recipient' })}
            {field('phone', 'Mobile Number *', {
              placeholder: '10-digit mobile number',
              maxLength: 10,
              inputMode: 'numeric',
              onChange: (e) => { if (/^\d{0,10}$/.test(e.target.value)) handleChange(e); },
            })}

            {/* ---- Address ---- */}
            <h3
              className="mt-7 mb-4 pt-5"
              style={{
                fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: 'var(--fx-ink-2)',
                borderTop: '1px solid var(--fx-line)',
              }}
            >
              Address
            </h3>

            {field('pincode', 'Pin Code *', {
              placeholder: '6-digit pincode',
              maxLength: 6,
              inputMode: 'numeric',
              onChange: (e) => { if (/^\d{0,6}$/.test(e.target.value)) handleChange(e); },
            })}

            <div className="fx-field relative">
              <label htmlFor="addr-address">Address (House No, Building, Street, Area) *</label>
              <textarea
                id="addr-address"
                name="address"
                rows={3}
                value={formData.address}
                onChange={(e) => searchAddress(e.target.value)}
                placeholder="Start typing your address…"
                className="fx-input"
                data-invalid={Boolean(errors.address)}
              />
              {errors.address && <p className="fx-error">{errors.address}</p>}

              <button
                type="button"
                onClick={() => setShowMapPicker(true)}
                className="mt-2 flex items-center gap-1.5"
                style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}
              >
                <MapPin size={14} />
                Pick location on map
              </button>

              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div
                  className="absolute z-20 w-full mt-2 bg-white max-h-80 overflow-y-auto hide-scrollbar"
                  style={{ border: '1px solid var(--fx-line)', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                >
                  <div
                    className="sticky top-0 bg-[#f5f5f6] px-4 py-2"
                    style={{ borderBottom: '1px solid var(--fx-line)' }}
                  >
                    <p style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                      {addressSuggestions.length} addresses found
                    </p>
                  </div>
                  {addressSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => selectSuggestion(suggestion)}
                      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#fafafa]"
                      style={{ borderBottom: '1px solid var(--fx-line)' }}
                    >
                      <MapPin size={16} style={{ color: 'var(--fx-ink-3)', marginTop: 2, flexShrink: 0 }} />
                      <span>
                        <span className="block" style={{ fontSize: 14, color: 'var(--fx-ink)' }}>
                          {suggestion.address}
                        </span>
                        <span className="block" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
                          Pincode: {suggestion.pincode}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {field('landmark', 'Locality / Landmark', { placeholder: 'Nearby landmark' })}

            <div className="grid grid-cols-2 gap-3">
              {field('city', 'City / District *', { placeholder: 'City' })}
              {field('state', 'State *', { placeholder: 'State' })}
            </div>

            {/* ---- Save as ---- */}
            <h3
              className="mt-7 mb-3 pt-5"
              style={{
                fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: 'var(--fx-ink-2)',
                borderTop: '1px solid var(--fx-line)',
              }}
            >
              Save Address As *
            </h3>

            <div className="flex gap-2.5 flex-wrap">
              {['Home', 'Work', 'Other'].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className="fx-chip"
                  data-selected={formData.label === preset}
                  onClick={() => {
                    setFormData({ ...formData, label: preset });
                    if (errors.label) setErrors({ ...errors, label: '' });
                  }}
                >
                  {preset}
                </button>
              ))}
            </div>

            <div className="fx-field mt-4">
              <input
                type="text"
                name="label"
                value={formData.label}
                onChange={handleChange}
                placeholder="Or type a custom label"
                className="fx-input"
                data-invalid={Boolean(errors.label)}
                aria-label="Custom address label"
              />
              {errors.label && <p className="fx-error">{errors.label}</p>}
            </div>
          </form>

          {/* Pinned actions */}
          <div
            className="fixed inset-x-0 bottom-0 bg-white flex gap-3 px-4 py-3 max-w-[560px] mx-auto"
            style={{ borderTop: '1px solid var(--fx-line)' }}
          >
            <button type="button" onClick={onClose} className="fx-btn fx-btn-ghost flex-1">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="fx-btn fx-btn-primary flex-1">
              {editAddress ? 'Save Address' : 'Add Address'}
            </button>
          </div>
        </div>
      </div>

      {showMapPicker && (
        <MapPickerModal
          onClose={() => setShowMapPicker(false)}
          onConfirm={handleMapConfirm}
          initialLat={formData.latitude || null}
          initialLng={formData.longitude || null}
        />
      )}
    </>
  );
};

export default AddAddressModal;
